import { useState } from "react";
import { Link } from "react-router-dom";
import { Copy, Check, ExternalLink, Wallet, ArrowUpRight, Layers, Coins, Sparkles, Coffee } from "lucide-react";
import {
    ADDRESSES,
    EXPLORER_ADDR,
    EXPLORER_TOKEN,
    dexSwapUrl,
    dexAddLpUrl,
    dexPairInfoUrl,
    marketplaceCollectionUrls,
} from "@/lib/config";
import { useAccount } from "wagmi";
import { formatEther } from "viem";
import { useMintInfo } from "@/hooks/useMint";

const PIGGY_DECIMALS = 18;
const PIGGY_SYMBOL = "PIGGY";

function shorten(addr: string, n = 4) {
    return `${addr.slice(0, 6)}…${addr.slice(-n)}`;
}

function CopyButton({ value, ariaLabel }: { value: string; ariaLabel: string }) {
    const [copied, setCopied] = useState(false);
    return (
        <button
            type="button"
            aria-label={ariaLabel}
            onClick={async () => {
                try {
                    await navigator.clipboard.writeText(value);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 1500);
                } catch {
                    /* clipboard blocked; user can still select address */
                }
            }}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white/[0.04] hover:bg-white/[0.08] text-piggy-cream/70 hover:text-piggy-cream text-xs"
        >
            {copied ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
            <span>{copied ? "Copied" : "Copy"}</span>
        </button>
    );
}

function ActionPill({ href, children, internal }: { href: string; children: React.ReactNode; internal?: boolean }) {
    if (internal) {
        return (
            <Link to={href} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full glass hover:bg-white/[0.05] text-piggy-cream/80 hover:text-piggy-cream text-xs">
                {children}
                <ArrowUpRight className="w-3 h-3" />
            </Link>
        );
    }
    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full glass hover:bg-white/[0.05] text-piggy-cream/80 hover:text-piggy-cream text-xs"
        >
            {children}
            <ExternalLink className="w-3 h-3" />
        </a>
    );
}

interface ContractRowProps {
    icon: React.ReactNode;
    title: string;
    description: string;
    address: string;
    actions: React.ReactNode;
    accent?: boolean;
}

function ContractRow({ icon, title, description, address, actions, accent }: ContractRowProps) {
    return (
        <div className={`glass rounded-2xl p-5 ${accent ? "ring-1 ring-piggy-pink-500/30" : ""}`}>
            <div className="flex items-start gap-4 mb-3 flex-wrap">
                <div className={`w-10 h-10 rounded-xl grid place-items-center flex-shrink-0 ${accent ? "bg-piggy-pink-500/20 text-piggy-pink-300" : "bg-white/[0.04] text-piggy-cream/60"}`}>
                    {icon}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="display text-lg font-bold text-piggy-cream">{title}</div>
                    <div className="text-xs text-piggy-cream/50">{description}</div>
                </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap mb-3 bg-black/20 rounded-lg px-3 py-2">
                <code className="font-mono text-xs text-piggy-cream/80 truncate flex-1 min-w-0" title={address}>
                    <span className="hidden md:inline">{address}</span>
                    <span className="md:hidden">{shorten(address, 6)}</span>
                </code>
                <CopyButton value={address} ariaLabel={`Copy ${title} address`} />
            </div>
            <div className="flex items-center gap-2 flex-wrap">
                {actions}
            </div>
        </div>
    );
}

// MetaMask wallet_watchAsset helper — adds PIGGY to the user's token list so
// the balance + transfer UI shows up next to ETH.
async function addPiggyToWallet(address: string) {
    const eth = (window as unknown as { ethereum?: { request: (args: { method: string; params: unknown }) => Promise<unknown> } }).ethereum;
    if (!eth?.request) {
        alert("No injected wallet detected. Use the PIGGY contract address above to add manually.");
        return;
    }
    try {
        await eth.request({
            method: "wallet_watchAsset",
            params: {
                type: "ERC20",
                options: {
                    address,
                    symbol: PIGGY_SYMBOL,
                    decimals: PIGGY_DECIMALS,
                    image: `${window.location.origin}/piggy.png`,
                },
            },
        });
    } catch {
        /* user rejected — no toast needed */
    }
}

export function ContractsPanel() {
    useAccount();
    const { info } = useMintInfo();
    const piggy = ADDRESSES.PIGGY_TOKEN;
    const pair = ADDRESSES.PIGGY_PAIR;
    const lpStake = ADDRESSES.LOCKED_LP_STAKING;
    const pStake = ADDRESSES.PIGGY_STAKING;
    const originals = ADDRESSES.PIGGY_ORIGINALS;

    if (!piggy) return null;

    const swapUrl = dexSwapUrl(piggy);
    const lpUrl = dexAddLpUrl(piggy);
    const pairInfoUrl = dexPairInfoUrl(pair);
    const market = marketplaceCollectionUrls(originals);

    return (
        <section className="mb-16">
            <div className="flex items-baseline justify-between mb-5 flex-wrap gap-2">
                <h2 className="display text-3xl md:text-4xl font-black">
                    Verified <span className="gradient-text">contracts</span>
                </h2>
                <span className="text-xs text-piggy-cream/40">Copy, swap, stake, view — all in one place.</span>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
                <ContractRow
                    icon={<Coins className="w-5 h-5" />}
                    title="PIGGY Token"
                    description="The ERC-20. Add to your wallet, swap on Uniswap, or transfer."
                    address={piggy}
                    accent
                    actions={
                        <>
                            <button
                                onClick={() => addPiggyToWallet(piggy)}
                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-piggy-pink-500/20 hover:bg-piggy-pink-500/30 text-piggy-pink-200 text-xs"
                            >
                                <Wallet className="w-3 h-3" /> Add to wallet
                            </button>
                            {swapUrl && (
                                <ActionPill href={swapUrl}>Swap on Uniswap</ActionPill>
                            )}
                            <ActionPill href={`${EXPLORER_TOKEN}/${piggy}`}>Explorer</ActionPill>
                        </>
                    }
                />

                {pair && (
                    <ContractRow
                        icon={<Layers className="w-5 h-5" />}
                        title="PIGGY / WETH LP Pair"
                        description="The Uniswap V2 LP. Provide liquidity to earn from swaps and stake for the LP boost."
                        address={pair}
                        actions={
                            <>
                                {lpUrl && <ActionPill href={lpUrl}>Add liquidity</ActionPill>}
                                <ActionPill href="/lp" internal>Stake LP</ActionPill>
                                {pairInfoUrl && <ActionPill href={pairInfoUrl}>Pair info</ActionPill>}
                                <ActionPill href={`${EXPLORER_ADDR}/${pair}`}>Explorer</ActionPill>
                            </>
                        }
                    />
                )}

                {pStake && (
                    <ContractRow
                        icon={<Coffee className="w-5 h-5" />}
                        title="PIGGY Single-Sided Staking"
                        description="Stake PIGGY, earn ETH from 20% of every taxed swap. No lockup."
                        address={pStake}
                        actions={
                            <>
                                <ActionPill href="/stake" internal>Open staking</ActionPill>
                                <ActionPill href={`${EXPLORER_ADDR}/${pStake}`}>Explorer</ActionPill>
                            </>
                        }
                    />
                )}

                {lpStake && (
                    <ContractRow
                        icon={<Layers className="w-5 h-5" />}
                        title="Locked LP Staking"
                        description="Lock LP for 30 / 90 / 180 days, earn ETH from 30% of every taxed swap with a tier multiplier."
                        address={lpStake}
                        actions={
                            <>
                                <ActionPill href="/lp" internal>Open LP staking</ActionPill>
                                <ActionPill href={`${EXPLORER_ADDR}/${lpStake}`}>Explorer</ActionPill>
                            </>
                        }
                    />
                )}

                {originals && (
                    <ContractRow
                        icon={<Sparkles className="w-5 h-5" />}
                        title="Piggy Originals (NFT)"
                        description={`5,000 ERC-721 piggies with on-chain LP-boost utility. Mint at ${formatEther(info.mintPrice)} ETH.`}
                        address={originals}
                        actions={
                            <>
                                <ActionPill href="/mint" internal>Mint</ActionPill>
                                {market && <ActionPill href={market.explorer}>Collection</ActionPill>}
                                <ActionPill href={`${EXPLORER_ADDR}/${originals}`}>Explorer</ActionPill>
                            </>
                        }
                    />
                )}
            </div>
        </section>
    );
}
